import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Link2, Plus } from "lucide-react";
import { toast } from "sonner";

const GROUPS = ["employment", "agency", "referral", "mentorship", "family", "other"];

export function RelationshipsPanel({ recordType, recordId, title = "Related Records", bare = false }) {
  const [items, setItems] = useState(null);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try {
      const [r, t] = await Promise.all([
        api.get("/relationships", { params: { record_type: recordType, record_id: recordId } }),
        api.get("/relationship-types"),
      ]);
      setItems(r.data.items); setTypes(t.data.items);
    } catch (e) { toast.error(apiError(e)); }
  }, [recordType, recordId]);
  useEffect(() => { load(); }, [load]);

  const end = async (rel) => { try { await api.patch(`/relationships/${rel.id}`, { end_date: new Date().toISOString().slice(0, 10) }); load(); toast.success("Marked historical"); } catch (e) { toast.error(apiError(e)); } };

  const inner = (
    <>
      <div className="flex items-center justify-between mb-3">
        {bare ? <span /> : <h3 className="font-display font-semibold text-sm flex items-center gap-1.5"><Link2 className="w-4 h-4 text-brand" /> {title}</h3>}
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="add-relationship"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
      <p className="text-xs text-slate-500 mb-3" data-testid="relationships-help">
        Structured connections to People and Businesses — e.g. their <b>district/office</b>, the person who <b>referred</b> them,
        anyone they <b>referred</b>, a <b>mentor</b>, or their <b>employing agency</b>. Each entry shows who they're connected to
        (click the name to open that record) and how they're related.
      </p>
      {items === null ? <p className="text-sm text-slate-400">Loading…</p> : items.length ? (
        <div className="space-y-3" data-testid="relationships-list">
          {GROUPS.filter((g) => items.some((i) => i.category === g)).map((g) => (
            <div key={g}>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">{g}</p>
              {items.filter((i) => i.category === g).map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2 mb-1" data-testid={`rel-${r.id}`}>
                  <span className="text-sm text-slate-700">
                    {r.label} →{" "}
                    {r.related_id ? (
                      <Link to={`${r.related_type === "business" ? "/agencies/detail/" : r.related_type === "opportunity" ? "/recruiting/opportunity/" : "/people/detail/"}${r.related_id}`}
                        className="font-semibold text-brand hover:underline" data-testid={`rel-link-${r.id}`}>{r.related_name}</Link>
                    ) : <b>{r.related_name}</b>}
                    {r.related_org_name && <span className="text-xs text-slate-400"> · {r.related_org_name}</span>}
                  </span>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={r.current ? "active" : "inactive"} label={r.current ? "Current" : "Historical"} />
                    {r.current && <button onClick={() => end(r)} className="text-xs text-slate-400 hover:text-rose-600" data-testid={`rel-end-${r.id}`}>End</button>}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : <EmptyState icon={Link2} title="No relationships" description="Connect this record to People or Businesses." />}
      {open && <AddRelationship recordType={recordType} recordId={recordId} types={types} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </>
  );

  if (bare) return <div data-testid="relationships-panel">{inner}</div>;
  return <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="relationships-panel">{inner}</div>;
}

function AddRelationship({ recordType, recordId, types, onClose, onDone }) {
  const [targetType, setTargetType] = useState("person");
  const [q, setQ] = useState(""); const [results, setResults] = useState([]);
  const [target, setTarget] = useState(null);
  const [relType, setRelType] = useState(types[0]?.key || "");
  const [saving, setSaving] = useState(false);

  const search = async (v) => {
    setQ(v); if (v.length < 2) return setResults([]);
    // Relationship pickers search ALL authorized Districts, not just the selected one.
    const url = targetType === "business" ? "/businesses" : "/persons";
    const params = targetType === "business"
      ? { q: v, all_districts: true }
      : { q: v, page_size: 10, all_districts: true };
    const { data } = await api.get(url, { params });
    setResults(targetType === "business" ? data.items : data.persons);
  };
  const optMeta = (r) => {
    if (targetType === "business") {
      return [r.business_type, r.organization_name, [r.city, r.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ");
    }
    return [(r.classifications || [])[0], r.organization_name].filter(Boolean).join(" · ");
  };
  const submit = async () => {
    if (!target || !relType) return;
    setSaving(true);
    try { await api.post("/relationships", { source_type: recordType, source_id: recordId, target_type: targetType, target_id: target.id, relationship_type: relType }); toast.success("Relationship added"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="add-relationship-dialog">
        <DialogHeader><DialogTitle>Add Relationship</DialogTitle><DialogDescription>Search an existing Person or Business — reciprocal side is shown automatically.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Select value={relType} onValueChange={setRelType}><SelectTrigger data-testid="rel-type"><SelectValue placeholder="Relationship" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">{types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent></Select>
            <Select value={targetType} onValueChange={(v) => { setTargetType(v); setTarget(null); setResults([]); setQ(""); }}><SelectTrigger data-testid="rel-target-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="person">Person</SelectItem><SelectItem value="business">Agency / Business</SelectItem></SelectContent></Select>
          </div>
          <div className="relative">
            <Input value={target ? (target.name || `${target.first_name} ${target.last_name}`) : q} onChange={(e) => { setTarget(null); search(e.target.value); }} placeholder="Search…" data-testid="rel-search" />
            {results.length > 0 && !target && (
              <div className="w-full bg-white border border-slate-200 rounded-md shadow-sm mt-1 max-h-52 overflow-y-auto" data-testid="rel-results">
                {results.map((r) => <button key={r.id} onClick={() => { setTarget(r); setResults([]); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm" data-testid={`rel-opt-${r.id}`}>
                  <span className="font-medium text-slate-700">{r.name || `${r.first_name} ${r.last_name}`}</span>
                  {optMeta(r) && <span className="block text-xs text-slate-400 capitalize">{optMeta(r)}</span>}
                </button>)}
              </div>
            )}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !target} data-testid="rel-submit" className="bg-brand hover:bg-brand-strong">Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
